import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search } from 'lucide-react';
import { AnnouncementBar } from '../components/AnnouncementBar';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { CTA } from '../components/CTA';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/Accordion';
type Category = 'Ordering' | 'Lab testing' | 'Shipping' | 'Storage & handling' | 'Research use';
const FAQS: Array<{
  category: Category;
  q: string;
  a: string;
}> = [{
  category: 'Ordering',
  q: 'Do I need a licence to order from peptides.ml?',
  a: 'Our peptides are sold strictly for in-vitro research use only. You must be 18+ and confirm at checkout that the material will not be used on humans or animals. Institutional purchase orders are welcome — contact us for net-terms.'
}, {
  category: 'Ordering',
  q: 'Can I order in bulk for a lab or research group?',
  a: 'Yes. Discounts kick in automatically at 3, 5, and 10 vials per product (10%, 15%, 20% off). For larger volumes or recurring orders, email purchasing@peptides.ml for a custom quote.'
}, {
  category: 'Ordering',
  q: 'What payment methods do you accept?',
  a: 'All major cards via Stripe, Apple Pay, Google Pay, and bank transfer for institutional orders. We do not store card details.'
}, {
  category: 'Lab testing',
  q: 'Where can I see the Certificate of Analysis for a batch?',
  a: 'On every product page, under the "Lab Verified" section. Each COA is tied to the exact batch number on your vial — not a stock COA from a previous batch. You can also enter any batch number in the verifier tool to retrieve it.'
}, {
  category: 'Lab testing',
  q: 'Which lab tests your peptides?',
  a: 'Our peptides are tested by an ISO 17025-accredited UK laboratory. We rotate between Eurofins UK and an independent partner facility to avoid single-lab bias. Both labs return chromatograms, mass-spec confirmation, and endotoxin readings.'
}, {
  category: 'Lab testing',
  q: 'What does "≥99% HPLC purity" actually mean?',
  a: 'High-performance liquid chromatography measures the proportion of your peptide of interest vs. any other peaks (impurities, fragments, salts) in the sample. ≥99% means at most 1% of the total peak area is non-target material. The full chromatogram for your batch is downloadable.'
}, {
  category: 'Shipping',
  q: 'How fast do you ship?',
  a: 'UK orders placed before 2pm GMT ship the same day via tracked next-day delivery. EU orders ship within 24h. International orders ship within 48h with full export paperwork.'
}, {
  category: 'Shipping',
  q: 'Do you ship internationally?',
  a: 'Yes — to most of the EU, North America, and select APAC countries. Customs paperwork is included; duties may apply at the destination. Check our shipping page for a country list.'
}, {
  category: 'Shipping',
  q: 'Is shipping discreet?',
  a: 'Yes. Plain outer packaging with no branding or product references. The shipping label lists "peptides.ml LTD" as the sender.'
}, {
  category: 'Storage & handling',
  q: 'How should I store unopened lyophilised vials?',
  a: 'Refrigerate at 2–8°C (standard fridge) for short-term storage (up to 3 months from receipt) or freeze at -20°C for long-term storage (up to 24 months). Keep out of direct light.'
}, {
  category: 'Storage & handling',
  q: 'How long is reconstituted peptide stable?',
  a: 'Most reconstituted peptides are stable for 4–8 weeks refrigerated at 2–8°C in bacteriostatic water. Specific shelf-life varies per peptide — refer to the storage card included with each shipment.'
}, {
  category: 'Research use',
  q: 'Can I use peptides.ml products on humans?',
  a: 'No. Our products are sold exclusively for in-vitro research use only. They are not pharmaceutical-grade, are not approved by the MHRA, FDA, or any regulator for human use, and must not be administered to humans or animals.'
}, {
  category: 'Research use',
  q: 'Do you offer COAs in a regulatory-acceptable format?',
  a: 'Our COAs are issued by an ISO 17025-accredited UK lab and meet the documentation standards required for academic and contract-research applications. For pharmaceutical submission requirements, contact us — additional documentation can be commissioned.'
}];
const CATEGORIES: Category[] = ['Ordering', 'Lab testing', 'Shipping', 'Storage & handling', 'Research use'];
export function FAQPage() {
  const [activeCategory, setActiveCategory] = useState<Category | 'All'>('All');
  const [query, setQuery] = useState('');
  const filtered = FAQS.filter(f => {
    const matchCategory = activeCategory === 'All' || f.category === activeCategory;
    const matchQuery = !query || f.q.toLowerCase().includes(query.toLowerCase()) || f.a.toLowerCase().includes(query.toLowerCase());
    return matchCategory && matchQuery;
  });
  return <div className="min-h-screen w-full bg-background" data-id="element-1306">
      <AnnouncementBar data-id="element-1307" />
      <Header data-id="element-1308" />

      <main data-id="element-1309">
        {/* Hero */}
        <section className="container mx-auto pt-14 lg:pt-20 pb-12 lg:pb-16" data-id="element-1310">
          <div className="max-w-2xl" data-id="element-1311">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-3" data-id="element-1312">
              Frequently asked
            </p>
            <h1 className="text-4xl lg:text-[56px] font-semibold tracking-[-0.03em] text-foreground leading-[1.05]" data-id="element-1313">
              Straight answers<span className="text-primary" data-id="element-1314">.</span>
            </h1>
            <p className="mt-5 text-[16px] leading-relaxed text-muted-foreground" data-id="element-1315">
              Everything researchers ask before ordering — ordering, lab
              testing, shipping, storage, and research-use boundaries.
              Can&apos;t find what you need? Email{' '}
              <a href="mailto:hello@peptides.ml" className="text-foreground underline underline-offset-2 hover:text-primary" data-id="element-1316">
                hello@peptides.ml
              </a>
              .
            </p>
          </div>

          {/* Search */}
          <div className="mt-10 flex flex-col sm:flex-row gap-4 items-stretch sm:items-center" data-id="element-1317">
            <div className="relative flex-1 max-w-md" data-id="element-1318">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.75} data-id="element-1319" />
              <input type="search" placeholder="Search questions…" value={query} onChange={e => setQuery(e.target.value)} className="w-full h-11 pl-10 pr-4 rounded-full border border-border bg-card text-[14px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-foreground/30 transition-colors" data-id="element-1320" />
            </div>
            <div className="flex flex-wrap gap-1.5" data-id="element-1321">
              {(['All', ...CATEGORIES] as const).map(c => {
              const active = c === activeCategory;
              return <button key={c} onClick={() => setActiveCategory(c)} className={`inline-flex items-center px-3 h-9 rounded-full text-[12.5px] font-medium transition-colors ${active ? 'bg-foreground text-background' : 'bg-card border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30'}`} data-id="element-1322">
                    {c}
                  </button>;
            })}
            </div>
          </div>
        </section>

        {/* List */}
        <section className="container mx-auto pb-20 lg:pb-28" data-id="element-1323">
          {filtered.length === 0 ? <div className="rounded-2xl border border-border bg-card p-10 text-center" data-id="element-1324">
              <p className="text-[15px] text-foreground font-medium" data-id="element-1325">
                No questions matched &ldquo;{query}&rdquo;.
              </p>
              <p className="text-[13px] text-muted-foreground mt-2" data-id="element-1326">
                Try a different keyword, or contact us directly.
              </p>
              <div className="mt-6 inline-flex" data-id="element-1327">
                <CTA href="mailto:hello@peptides.ml" variant="primary" size="md" data-id="element-1328">
                  Ask a researcher
                </CTA>
              </div>
            </div> : <motion.div initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3
        }} className="rounded-2xl border border-border bg-card overflow-hidden" data-id="element-1329">
              <Accordion type="single" className="w-full" data-id="element-1330">
                {filtered.map((f, i) => <AccordionItem key={i} value={`q-${i}`} className="border-border border-b last:border-b-0 px-6 lg:px-8" data-id="element-1331">
                    <AccordionTrigger className="text-left text-[15px] font-medium hover:no-underline py-5" data-id="element-1332">
                      <span className="flex items-center gap-4 flex-1 text-left" data-id="element-1333">
                        <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-medium min-w-[110px]" data-id="element-1334">
                          {f.category}
                        </span>
                        <span className="flex-1" data-id="element-1335">{f.q}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-[14px] text-muted-foreground leading-relaxed pl-[126px] pr-2 pb-5" data-id="element-1336">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </motion.div>}
        </section>

        {/* Contact strip */}
        <section className="w-full bg-card border-y border-border" data-id="element-1337">
          <div className="container mx-auto py-12 lg:py-14 grid lg:grid-cols-[1.5fr_1fr] gap-8 items-center" data-id="element-1338">
            <div data-id="element-1339">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-2" data-id="element-1340">
                Still stuck?
              </p>
              <h2 className="text-2xl lg:text-3xl font-semibold tracking-tight text-foreground leading-tight" data-id="element-1341">
                Talk to a real chemist, not a chatbot.
              </h2>
              <p className="mt-3 text-[14px] text-muted-foreground max-w-md" data-id="element-1342">
                Our support is staffed by people with actual lab experience.
                Average response time: under 4 working hours.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end" data-id="element-1343">
              <CTA href="mailto:hello@peptides.ml" variant="primary" size="lg" data-id="element-1344">
                Email us
              </CTA>
              <CTA to="/about" variant="secondary" size="lg" data-id="element-1345">
                About peptides.ml
              </CTA>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1346" />
    </div>;
}